import { useMount } from 'ahooks';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ScenarioConfigContext, ScenarioConfigEditMeta } from './ScenarioConfigContextV2';
import { getScenarioConfigs, updateScenarioConfigs } from '@knowledge/service/api/activity';
import { message, Switch, Tabs } from 'antd';
import useModal from 'hooks/useModal';
import { Formik, FormikProps } from 'formik';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { JustRightHeightModal } from 'components/styled/scoped';
import { useGetTitle } from './PolicyForm/share';

export function ScenarioConfigProvider({ enable, children }: { enable: boolean; children: any }) {
  const { t } = useTranslate();

  const [scenarioConfigs, setScenarioConfigs] = useState<Knowledge.Policy.ScenarioConfig[]>([]);

  const refreshScenarioConfigs = useCallback(() => {
    return getScenarioConfigs()
      .then(({ scenarioConfig }) => {
        setScenarioConfigs(scenarioConfig);
      })
      .catch(() => {});
  }, []);

  useMount(refreshScenarioConfigs);

  const { visible, show, hide, meta } = useModal<ScenarioConfigEditMeta>();

  const modal = meta && (
    <ScenarioConfigForm {...meta} onUpdate={() => hide()}>
      {(form, { isSubmitting, dirty, handleSubmit, resetForm }) => {
        return (
          <JustRightHeightModal
            width={780}
            title={t('生成话术时机')}
            visible={visible}
            onCancel={() => {
              hide();
              resetForm();
            }}
            okText={t('保存')}
            okButtonProps={{
              loading: isSubmitting,
              disabled: !dirty,
              onClick: () => handleSubmit(),
            }}
          >
            {form}
          </JustRightHeightModal>
        );
      }}
    </ScenarioConfigForm>
  );

  return (
    <ScenarioConfigContext.Provider
      value={useMemo(() => {
        return {
          scenarioConfigEnable: enable,
          scenarioConfigs,
          setScenarioConfigs,
          refreshScenarioConfigs,
          editScenarioConfigs: show,
        };
      }, [enable, scenarioConfigs, refreshScenarioConfigs, show])}
    >
      {children}
      {modal}
    </ScenarioConfigContext.Provider>
  );
}

export function useScenarioConfigContext() {
  const value = useContext(ScenarioConfigContext);

  if (!value) {
    throw new Error(
      'useScenarioConfigContext should be used in a context of ScenarioConfigProvider component',
    );
  }

  return value;
}

type FV = Record<
  Knowledge.Policy.ActivitySendPoint,
  { scenarioType: Knowledge.Policy.ScenarioType; enable: boolean }[]
>;

const liveSendPoints: Knowledge.Policy.ActivitySendPoint[] = [
  'EFFECTIVE_IN_QUANTUM',
  'EFFECTIVE_NOT_IN_QUANTUM',
  'WARM_UP',
];
const activitySendPoints: Knowledge.Policy.ActivitySendPoint[] = ['EFFECTIVE', 'WARM_UP'];

export function ScenarioConfigForm({
  activityFormValues,
  sendPointScenariosMap,
  children,
  onUpdate,
}: ScenarioConfigEditMeta & {
  children: (form: ReactNode, formProps: FormikProps<FV>) => ReactNode;
  onUpdate?: () => void;
}) {
  const { t, getSendPointLabel, getScenarioTypeLabel } = useGetTitle();

  const { scenarioConfigs, setScenarioConfigs } = useScenarioConfigContext();

  const isLive = activityFormValues.activityType === 'LIVE_STREAMING_ACTIVITY';

  const initialValues = useMemo(() => {
    return Object.keys(sendPointScenariosMap).reduce<FV>((res, sendPoint) => {
      res[sendPoint] = sendPointScenariosMap[sendPoint].map((scenarioType) => {
        const target = scenarioConfigs.find((scenarioConfig) => {
          return (
            scenarioConfig.scenario ===
            `${isLive ? 'LIVE_STREAMING_ACTIVITY_' : ''}${scenarioType}_${sendPoint}`
          );
        });
        if (target) {
          return { scenarioType, enable: target.enable };
        }
        return { scenarioType, enable: true };
      });
      return res;
    }, {} as any);
  }, [sendPointScenariosMap, scenarioConfigs, isLive]);

  const [activeTab, setActiveTab] = useState(isLive ? 'EFFECTIVE_IN_QUANTUM' : 'EFFECTIVE');
  useEffect(() => {
    setActiveTab(isLive ? 'EFFECTIVE_IN_QUANTUM' : 'EFFECTIVE');
  }, [isLive, initialValues]);

  const containerRef = useRef<HTMLDivElement>(null);

  const sendPoints = isLive ? liveSendPoints : activitySendPoints;

  const getSendPoint = useCallback(
    (sendPoint: Knowledge.Policy.ActivitySendPoint) =>
      sendPoint === 'WARM_UP' && isLive ? 'LIVE_WARM_UP' : sendPoint,
    [isLive],
  );

  useEffect(() => {
    const scrollElement = containerRef.current?.parentElement;

    if (!scrollElement) {
      return;
    }

    const scrollHandler = () => {
      const scrollTop = scrollElement.scrollTop;
      const elements = containerRef.current?.children[0].children;
      if (!elements?.length) {
        return;
      }
      const elementArr = [...elements];
      const index = elementArr.findIndex((_, index) => {
        const height = elementArr
          .slice(0, index + 1)
          .reduce((res, element) => res + element.clientHeight, 0);
        return scrollTop <= height;
      });
      const activeTab = sendPoints.filter((sendPoint) => initialValues[sendPoint].length)[index];
      setActiveTab(getSendPoint(activeTab));
    };

    scrollElement.addEventListener('scroll', scrollHandler);

    return () => scrollElement.removeEventListener('scroll', scrollHandler);
  }, [sendPoints, initialValues, getSendPoint]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) => {
        const valueMap = Object.keys(values).reduce<Partial<Record<string, boolean>>>(
          (res, sendPoint) => {
            return values[sendPoint].reduce((res, { scenarioType, enable }) => {
              res[`${isLive ? 'LIVE_STREAMING_ACTIVITY_' : ''}${scenarioType}_${sendPoint}`] =
                enable;
              return res;
            }, res);
          },
          {},
        );
        const newScenarioConfigs = scenarioConfigs.map((scenarioConfig) => {
          const enable = valueMap[scenarioConfig.scenario];
          return enable === undefined
            ? scenarioConfig
            : {
                ...scenarioConfig,
                enable,
              };
        });
        return updateScenarioConfigs({ scenarioConfig: newScenarioConfigs })
          .then(() => {
            setScenarioConfigs(newScenarioConfigs);
            message.success(t('phrase.保存成功'));
            onUpdate?.();
          })
          .catch(() => {
            message.error(t('phrase.保存失败，请稍后再试'));
          });
      }}
    >
      {(formProps) => {
        const { values, setValues, handleSubmit } = formProps;

        const handleSubmitWithValidate = () => {
          if (
            Object.keys(values).every((key) => {
              return values[key].every(({ enable }) => !enable);
            })
          ) {
            message.error(t('请至少开启1项'));
          } else {
            handleSubmit();
          }
        };

        const getSendPoint = (sendPoint: Knowledge.Policy.ActivitySendPoint) =>
          sendPoint === 'WARM_UP' && isLive ? 'LIVE_WARM_UP' : sendPoint;

        const tabs = (
          <Tabs
            size="small"
            tabPosition="right"
            activeKey={activeTab}
            onChange={(key) => {
              const index = sendPoints
                .filter((sendPoint) => values[sendPoint].length)
                .findIndex((sendPoint) => getSendPoint(sendPoint) === key);
              if (index > -1) {
                containerRef.current?.children[0].children[index].scrollIntoView({
                  behavior: 'smooth',
                });
              }
            }}
          >
            {sendPoints.map((sendPoint) => {
              return (
                values[sendPoint].length > 0 && (
                  <Tabs.TabPane
                    key={getSendPoint(sendPoint)}
                    tab={getSendPointLabel(sendPoint, isLive)}
                  />
                )
              );
            })}
          </Tabs>
        );

        const contents = sendPoints.map((sendPoint) => {
          return (
            values[sendPoint].length > 0 && (
              <div>
                <h3>
                  <div>{getSendPointLabel(sendPoint, isLive)}</div>
                  <span>{t('关闭后将不再生成此时机的专属销售方案')}</span>
                </h3>
                <ul>
                  {values[sendPoint].map(({ scenarioType, enable }, index) => {
                    return (
                      <li>
                        <div>{getScenarioTypeLabel(scenarioType, activityFormValues)}</div>
                        <Switch
                          checked={enable}
                          onChange={(enable) => {
                            setValues({
                              ...values,
                              [sendPoint]: [
                                ...values[sendPoint].slice(0, index),
                                { scenarioType, enable },
                                ...values[sendPoint].slice(index + 1),
                              ],
                            });
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            )
          );
        });

        const form = (
          <Container ref={containerRef}>
            <section>{contents}</section>
            {tabs}
          </Container>
        );

        return children(form, {
          ...formProps,
          handleSubmit: handleSubmitWithValidate,
        });
      }}
    </Formik>
  );
}

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  margin: -24px;
  padding: 12px 16px;
  > .ant-tabs {
    flex: 0 0 auto;
    position: sticky;
    top: 0;
  }
  > section {
    flex: 1 0 0;
    overflow: hidden;
    > div {
      margin-bottom: 12px;
      padding: 12px 16px;
      background: var(--fett-erp-card-bg);
      > h3 {
        display: flex;
        justify-content: space-between;
        align-items: center;
        > div {
          flex: 0 0 auto;
          position: relative;
          margin-right: 12px;
          text-indent: 12px;
          font-weight: 500;
          &::before {
            content: '';
            display: inline-block;
            position: absolute;
            top: 5px;
            left: 0;
            width: 4px;
            height: 16px;
            background: var(--ant-primary-color);
          }
        }
        > span {
          flex: 1 0 0;
          text-align: right;
          font-size: 12px;
          font-weight: 400;
          color: var(--fett-text-tertiary);
          overflow: hidden;
          white-space: pre-wrap;
        }
      }
      > ul {
        margin: 0;
        padding: 0;
        list-style: none;
        > li {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 16px;
          background: var(--fett-bg-panel);
          margin-top: 2px;
          > div {
            flex: 1 0 0;
            margin-right: 16px;
            overflow: hidden;
            white-space: pre-wrap;
          }
        }
      }
    }
  }
`;
